import { EvesButton } from '@/common/components/atoms/button/button';
import { Grid, IconButton, Box as MuiBox, Button } from '@mui/material';
import Modal from '@mui/material/Modal';
import { Box } from '@mui/system';
import { CSSProperties, useState, useEffect } from 'react';
import { Close, CheckCircle } from '@mui/icons-material';
import { retryBillingError, reprocessTransactions, recalculateTransfers } from "@/services/invoices";
import store from '@/store';
import { setLoader, showNotification } from '@/store/app/reducer';
import { useSelector } from 'react-redux';
import { IAppContextModel } from '@/store/app/types';
import { Loader } from '@/common/components/atoms/loader/loader';

interface IRetryModalProps {
	onClose: () => void;
	isRetryModalOpen: boolean;
	selectedRowData?: any;
	onRetrySuccess?: () => void;
	sessionID: string;
	selectedSiteId: string;
	selectedBillingData?: any;
}

const RetryModal = (props: IRetryModalProps) => {
	const [open, setOpen] = useState(props.isRetryModalOpen);
	const appContext: IAppContextModel = useSelector(
		(state: any) => state.appContext
	);

	useEffect(() => {
		setOpen(props.isRetryModalOpen);
	}, [props.isRetryModalOpen]);

	const handleClose = () => {
		setOpen(false);
		props.onClose();
	};

	const handleRetryProceed = async () => {
		if (!props.sessionID) {
			store.dispatch(
				showNotification({
					showNotification: true,
					message: 'Session ID is required',
					type: 'error',
				})
			);
			return;
		}

		store.dispatch(setLoader(true));
		try {
			const response: any = await retryBillingError(props.sessionID, {
				sessionID: props.sessionID,
			});
			if (response?.status === "Success") {
				store.dispatch(
					showNotification({
						showNotification: true,
						message: response?.message,
						type: 'success',
					})
				);


				handleClose();
				if (props.onRetrySuccess) {
					props.onRetrySuccess();
				}
			} else {
				const errorMessage = response?.data && typeof response?.data === "string" ? JSON.parse(response?.data)?.errorMessage : "We couldn’t regenerate the bill. Please check logs and try again.";

				store.dispatch(
					showNotification({
						showNotification: true,
						message: errorMessage,
						type: "error",
					}),
				);

				handleClose();
			}
		} catch (error: any) {
			store.dispatch(
				showNotification({
					showNotification: true,
					message: "We couldn’t regenerate the bill. Please check logs and try again.",
					type: 'error',
				})
			);
		} finally {
			store.dispatch(setLoader(false));
		}
	};

	const handleReprocessProceed = async () => {
		if (!props.selectedSiteId) {
			store.dispatch(
				showNotification({
					showNotification: true,
					message: "Site ID is required to reprocess transactions",
					type: "error",
				}),
			);
			return;
		}

		store.dispatch(setLoader(true));
		try {
			const response: any = await reprocessTransactions(props.selectedSiteId);
			if (response?.status === "Success") {
				store.dispatch(
					showNotification({
						showNotification: true,
						message: response?.message,
						type: "success",
					}),
				);

				handleClose();
				if (props.onRetrySuccess) {
					props.onRetrySuccess();
				}
			} else {
				const errorMessage = response?.data && typeof response?.data === "string" ? JSON.parse(response?.data)?.errorMessage : "We couldn’t reprocess the transaction. Please try again.";

				store.dispatch(
					showNotification({
						showNotification: true,
						message: errorMessage,
						type: "error",
					}),
				);

				handleClose();
			}
		} catch (error: any) {
			store.dispatch(
				showNotification({
					showNotification: true,
					message: "We couldn’t reprocess the transaction. Please try again.",
					type: "error",
				}),
			);
		} finally {
			store.dispatch(setLoader(false));
		}
	};

	const handlePaymentConfiguration = async () => {
		const transferId = props.selectedBillingData?.stop?.invoiceItem?.accountData?.transferID ? props.selectedBillingData?.stop?.invoiceItem?.accountData?.transferID : "";

		if (!transferId) {
			store.dispatch(
				showNotification({
					showNotification: true,
					message: "Transfer ID is required to proceed with the payment configuration",
					type: "error",
				}),
			);
			return;
		}

		store.dispatch(setLoader(true));
		try {
			const response: any = await recalculateTransfers(transferId);
			if (response?.status === "Success") {
				store.dispatch(
					showNotification({
						showNotification: true,
						message: response?.message,
						type: "success",
					}),
				);

				handleClose();
				if (props.onRetrySuccess) {
					props.onRetrySuccess();
				}
			} else {
				const errorMessage = response?.data && typeof response?.data === "string" ? JSON.parse(response?.data)?.errorMessage : "We couldn’t reprocess the transaction. Please try again.";

				store.dispatch(
					showNotification({
						showNotification: true,
						message: errorMessage,
						type: "error",
					}),
				);

				handleClose();
			}
		} catch (error: any) {
			store.dispatch(
				showNotification({
					showNotification: true,
					message: "We couldn’t reprocess the transaction. Please try again.",
					type: "error",
				}),
			);
		} finally {
			store.dispatch(setLoader(false));
		}
	}

	const style = {
		modalBox: {
			position: 'absolute',
			top: '50%',
			left: '50%',
			transform: 'translate(-50%, -50%)',
			width: props.selectedRowData?.onAction === "handlePayment" ? '520px' : '400px',
			maxWidth: '90vw',
			bgcolor: 'white',
			boxShadow: "0 20px 24px -4px rgba(16, 24, 40, 0.08), 0 8px 8px -4px rgba(16, 24, 40, 0.03)",
			borderRadius: '12px',
			p: 3,
		},
		iconContainer: {
			display: 'flex',
			justifyContent: 'flex-start',
			alignItems: 'center',
		} as CSSProperties,
		successIcon: {
			width: '56px',
			height: '56px',
			borderRadius: '50%',
			backgroundColor: 'rgba(74, 222, 128, 0.1)',
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			position: 'relative',
		} as CSSProperties,
		checkmark: {
			color: '#4ADE80',
			fontSize: '32px',
		} as CSSProperties,
		header: {
			fontSize: '18px',
			fontWeight: '600',
			marginBottom: '8px',
			margin: 0,
			color: '#000',
		} as CSSProperties,
		message: {
			fontSize: '14px',
			color: '#666',
			marginBottom: '0',
			lineHeight: '1.5',
		} as CSSProperties,
		sessionId: {
			fontWeight: '600',
			color: '#000',
		} as CSSProperties,
		buttonContainer: {
			display: 'flex',
			justifyContent: 'space-between',
			gap: '12px',
			marginTop: '0',
		} as CSSProperties,
		cancelButton: {
			flex: 1,
			height: '44px',
			fontSize: '14px',
			textTransform: 'none',
			fontWeight: '500',
			backgroundColor: 'white !important',
			borderRadius: '8px',
			color: '#333 !important',
			border: '1px solid #D0D0D0 !important',
			'&:hover': {
				backgroundColor: '#F5F5F5 !important',
				border: '1px solid #D0D0D0 !important',
			},
		},
		proceedButton: {
			flex: 1,
			height: '44px',
			fontSize: '14px',
			textTransform: 'none',
			fontWeight: '500',
			borderRadius: '8px',
		} as CSSProperties,
		closeIcon: {
			position: 'absolute',
			right: '8px',
			top: '8px',
			cursor: 'pointer',
			color: '#666',
		} as CSSProperties,
	};

	const handleOnAction = (action: string) => {
		switch (action) {
			case "handlePayment":
				handlePaymentConfiguration();
				break;
			case "handleReprocess":
				handleReprocessProceed();
				break;
			case "handleRetry":
				handleRetryProceed();
				break;
			default:
				break;
		}
	}

	return (
		<>
			<Modal
				open={open}
				onClose={handleClose}
				aria-labelledby="retry-billing-modal"
				aria-describedby="retry-billing-description"
			>
				<>
					<Box sx={style.modalBox} data-cy='retry-billing-error-modal'>
						<IconButton
							onClick={handleClose}
							sx={style.closeIcon}
							data-cy="retry-modal-close-icon"
						>
							<Close />
						</IconButton>

						<Grid container spacing={2.5} direction={'column'}>
							{/* Success Icon */}
							<Grid item xs={12}>
								<div style={style.iconContainer}>
									<MuiBox sx={style.successIcon}>
										<CheckCircle sx={style.checkmark} />
									</MuiBox>
								</div>
							</Grid>

							{/* Title and Message */}
							<Grid item xs={12}>
								<h3 style={style.header}>{props.selectedRowData?.title}</h3>
								<div style={style.message}>
									{props.selectedRowData?.description.replace("<sessionID>", props.sessionID)}
								</div>
							</Grid>

							{/* Buttons */}
							<Grid item xs={12}>
								<div style={style.buttonContainer}>
									<Button
										onClick={handleClose}
										variant="outlined"
										sx={style.cancelButton}
										data-cy='retry-cancel-button'
									>
										{props.selectedRowData?.cancelAction}
									</Button>
									<EvesButton
										onClick={() => handleOnAction(props.selectedRowData?.onAction)}
										variant="contained"
										sx={{
											...style.proceedButton,
											'&.MuiButtonBase-root': {
												fontSize: '14px',
												borderRadius: '8px',
											},
										}}
										data-cy='retry-proceed-button'
									>
										{props.selectedRowData?.action}
									</EvesButton>
								</div>
							</Grid>
						</Grid>
					</Box>
					<Loader showLoader={appContext?.showLoader} />
				</>
			</Modal>
		</>
	);
};

export default RetryModal;
